import type { Maybe } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { FormController } from '@adornis/forms/x-form-controller.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { ChancenDialogTimeMatrix } from '@chancenwerk/client/components/dialogs/chancen-dialog-time-matrix.js';
import '@chancenwerk/components/chancen-component-choose-digital-courses.js';
import { Subject } from '@chancenwerk/db/courses/CourseDigital.js';
import { CourseGraduation } from '@chancenwerk/db/courses/CourseGraduation.js';
import type { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import ImageClientsSupport from './../assets/Classroom-med-min.png';
import { AnmeldungPageRegistrationBase } from './anmeldung-component-formular-base.js';

/**
 * @customElement anmeldung-component-formular-graduation-subjects
 */
@customElement('anmeldung-component-formular-graduation-subjects')
export class AnmeldungPageRegistrationDigitalCourses extends AnmeldungPageRegistrationBase {
  @property({ attribute: false }) form: Maybe<FormController<Student>>;
  @property({ attribute: false }) translation: Maybe<TranslationController<any>>;
  @property({ attribute: false }) student: Maybe<RXController<Student>>;
  @property({ attribute: false }) subjects: string[] = [];
  @property({ attribute: false }) bundesland: string = '';

  @state() private _choices = new RXController(
    this,
    CourseGraduation.subscribeAll<CourseGraduation>()({
      startDate: 1,
      startTime: 1,
      endTime: 1,
      subject: 1,
      bundesland: 1,
    }),
  );

  private _freetextSubject() {
    if (!this.form || !this.student || (this.student.value.schoolInformation.jahrgangsstufe ?? 0) < 10) {
      return nothing;
    }

    return html`<cr-input
      placeholder="weiterer Bedarf"
      ${this.form.field('graduationInformation', 'openSubjectGraduation')}
    ></cr-input>`;
  }

  private _is9or10() {
    return (
      this.student?.value.schoolInformation.jahrgangsstufe === 10 ||
      this.student?.value.schoolInformation.jahrgangsstufe === 9
    );
  }

  private _subjectSelection() {
    if (!this.form || !this.translation || !this.student) {
      return nothing;
    }

    if (this._is9or10()) {
      return html`<cr-grid columns="1fr 1fr" space="md">
        <cr-dropdown-selection
          clearable
          placeholder="Erstwahl*"
          ${this.form.field('graduationInformation', 'firstSubjectGraduation')}
          .selectables=${this.subjects}
        ></cr-dropdown-selection>
        <cr-dropdown-selection
          clearable
          placeholder="Kurs"
          ${this.form.field('graduationInformation', 'firstSubjectGraduationCourseType')}
          .selectables=${['Kurs G', 'Kurs E']}
        ></cr-dropdown-selection>
        <cr-dropdown-selection
          clearable
          placeholder="Zweitwahl"
          ${this.form.field('graduationInformation', 'secondSubjectGraduation')}
          .selectables=${this.subjects}
        ></cr-dropdown-selection>
        <cr-dropdown-selection
          clearable
          placeholder="Kurs"
          ${this.form.field('graduationInformation', 'secondSubjectGraduationCourseType')}
          .selectables=${['Kurs G', 'Kurs E']}
        ></cr-dropdown-selection>
        <cr-dropdown-selection
          clearable
          placeholder="Drittwahl"
          ${this.form.field('graduationInformation', 'thirdSubjectGraduation')}
          .selectables=${this.subjects}
        ></cr-dropdown-selection>
        <cr-dropdown-selection
          clearable
          placeholder="Kurs"
          ${this.form.field('graduationInformation', 'thirdSubjectGraduationCourseType')}
          .selectables=${['Kurs G', 'Kurs E']}
        ></cr-dropdown-selection>
      </cr-grid>`;
    }

    return html` <cr-dropdown-selection
        clearable
        placeholder="Erstwahl*"
        ${this.form.field('graduationInformation', 'firstSubjectGraduation')}
        .selectables=${this.subjects}
      ></cr-dropdown-selection>
      <cr-dropdown-selection
        clearable
        placeholder="Zweitwahl"
        ${this.form.field('graduationInformation', 'secondSubjectGraduation')}
        .selectables=${this.subjects}
      ></cr-dropdown-selection>
      <cr-dropdown-selection
        clearable
        placeholder="Drittwahl"
        ${this.form.field('graduationInformation', 'thirdSubjectGraduation')}
        .selectables=${this.subjects}
      ></cr-dropdown-selection>
      ${this._freetextSubject()}`;
  }

  private _courseInfos() {
    if (!this._is9or10()) {
      return nothing;
    }

    const choosenSubjects = [
      this.student?.value.graduationInformation?.firstSubjectGraduation,
      this.student?.value.graduationInformation?.secondSubjectGraduation,
      this.student?.value.graduationInformation?.thirdSubjectGraduation,
    ].filter(Boolean);

    const subjectIcons = {
      [Subject.MATHE]: 'calculate',
      [Subject.DEUTSCH]: 'description',
      [Subject.ENGLISH]: 'description',
      [Subject.PHYSIK]: 'blur_medium',
      [Subject.CHEMIE]: 'calculate',
      [Subject.BIOLOGIE]: 'microbiology',
    };

    if (choosenSubjects.length === 0) {
      return nothing;
    }

    return html`<cr-flex space="md">
      <cr-text bold> Für deine Fäche gibt es folgende Termininformationen: </cr-text>
      <cr-grid columns="1fr 1fr 1fr" space="md">
        ${(this._choices.value ?? [])
          .filter(c => {
            if (!choosenSubjects.includes(c.subject)) {
              return false;
            }

            if (!c.bundesland) {
              return true;
            }

            return c.bundesland === this.bundesland;
          })
          .map(c => {
            return html`
              <cr-flex
                horizontal
                space="sm"
                padding="md"
                ${css({ backgroundColor: this.colors.neutral.shade(50), borderRadius: '5px' })}
              >
                <cr-icon ${css({ fontSize: '2rem' })}> ${subjectIcons[c.subject ?? ''] ?? ''} </cr-icon>
                <cr-flex space="sm" w100 ${css({ paddingTop: this.spacing.sm })}>
                  <cr-text
                    ${css({
                      display: 'block',
                      fontSize: '1.2rem',
                      marginBottom: this.spacing.xs,
                      borderBottom: '1px solid rgba(0,0,0,0.1)',
                      paddingBottom: this.spacing.md,
                    })}
                  >
                    ${c.subject}
                  </cr-text>
                  <cr-text ${css({ fontSize: '1rem' })}> ${c.startTime?.toFormat('dd.MM.yyyy')} </cr-text>
                  <cr-text bold ${css({ opacity: 0.5, marginTop: this.spacing.sm })}>
                    ${c.startTime?.toFormat('HH:mm')} - ${c.endTime?.toFormat('HH:mm')}
                  </cr-text>
                </cr-flex>
              </cr-flex>
            `;
          })}
      </cr-grid>
    </cr-flex>`;
  }

  override render() {
    if (!this.form || !this.translation || !this.student) {
      return nothing;
    }

    return html`
      <cr-flex class="section-container" space="xl">
        <cr-image disabled .value=${ImageClientsSupport} mode="contain"> </cr-image>

        <cr-flex class="sc-content">
          <cr-flex space="lg">
            <cr-flex space="md">
              <cr-text ${css({ textAlign: 'justify' })} bold> Für welche Fächer möchtest du dich anmelden? </cr-text>
              ${this._subjectSelection()}
            </cr-flex>

            ${this._is9or10()
              ? nothing
              : html`<cr-flex space="md">
                    <cr-text ${css({ textAlign: 'justify' })} bold> Bitte wähle deine zeitliche Verfügbarkeit </cr-text>
                    <cr-button
                      mode="outline"
                      @click=${async () => {
                        if (!this.student) {
                          return;
                        }

                        const student = await ChancenDialogTimeMatrix.showPopup<Student>({
                          props: {
                            student: this.student.value,
                            headingText: 'Wähle deine zeitliche Verfügbarkeit für den CHANCENabschluss',
                            path: ['graduationInformation', 'daysAvailable'],
                            dateOffers: [
                              DateTime.now().startOf('day').plus({ hours: 16, minutes: 0 }),
                              DateTime.now().startOf('day').plus({ hours: 17, minutes: 30 }),
                              DateTime.now().startOf('day').plus({ hours: 18, minutes: 30 }),
                            ],
                          },
                        });
                        this.student.next(student);
                        this.requestUpdate();
                      }}
                    >
                      zeitliche Verfügbarkeit wählen
                    </cr-button>
                  </cr-flex>

                  <cr-flex space="md">
                    <cr-text ${css({ textAlign: 'justify' })} bold>
                      Falls du deine Prüfungsthemen weißt, trage diese ein:
                    </cr-text>
                    <cr-input
                      placeholder="Prüfungsthemen für Erstwahl"
                      ${this.form.field('graduationInformation', 'graduationFirstTestTopic')}
                    ></cr-input>
                    <cr-input
                      placeholder="Prüfungsthemen für Zweitwahl"
                      ${this.form.field('graduationInformation', 'graduationSecondTestTopic')}
                    ></cr-input>
                    <cr-input
                      placeholder="Prüfungsthemen für Drittwahl"
                      ${this.form.field('graduationInformation', 'graduationThirdTestTopic')}
                    ></cr-input>
                  </cr-flex>`}
            ${this._courseInfos()}

            <cr-flex horizontal end crossaxis-center space="sm">
              <cr-button
                @click=${async () => {
                  this.form?.validate().catch(console.error);
                }}
              >
                ${this.translation.translate('next')}
              </cr-button>
            </cr-flex>
          </cr-flex>
        </cr-flex>
      </cr-flex>
    `;
  }
}
